import React from "react";
import type { GameReach } from "../api/types";
import { pushCounts, pushNeedsApp } from "./reachRules";

type Tone = "ok" | "warn" | "bad" | "off";

interface Props {
  reach: GameReach | null;
  errorMessage: string | null;
  checking: boolean;
  /** Notification.permission on this device ("denied" means blocked here). */
  localPermission: NotificationPermission | "unsupported";
  onRecheck: () => void;
}

function pushLine(reach: GameReach | null, localPermission: Props["localPermission"]): [Tone, string] {
  if (reach && pushCounts(reach)) {
    const n = reach.push.activeDevices;
    return ["ok", `Notifications on this site: on for ${n} ${n === 1 ? "device" : "devices"}`];
  }
  if (pushNeedsApp(reach)) return ["warn", "Notifications on this site: on in a browser, but the game needs the app"];
  if (localPermission === "denied") return ["bad", "Notifications on this site: blocked in this browser"];
  if (reach?.push.state === "lost") return ["warn", "Notifications on this site: off, they stopped working"];
  return ["off", "Notifications on this site: off"];
}

function telegramLine(reach: GameReach | null): [Tone, string] {
  switch (reach?.telegram.state) {
    case "ok":
      return ["ok", "Telegram: linked"];
    case "muted":
      return ["warn", "Telegram: linked but muted"];
    case "blocked":
      return ["bad", "Telegram: linked, but the bot is blocked"];
    default:
      return ["off", "Telegram: not linked"];
  }
}

/** Live status for step 2: what the game can use to reach this person right now. */
const ReachStatus: React.FC<Props> = ({ reach, errorMessage, checking, localPermission, onRecheck }) => {
  const [pushTone, push] = pushLine(reach, localPermission);
  const [tgTone, tg] = telegramLine(reach);
  return (
    <div className="gm-status" aria-live="polite" data-testid="reach-status">
      {reach ? (
        <>
          <div className="gm-status__row">
            <span className="gm-status__dot" data-tone={pushTone} aria-hidden />
            <span>{push}</span>
          </div>
          <div className="gm-status__row">
            <span className="gm-status__dot" data-tone={tgTone} aria-hidden />
            <span>{tg}</span>
          </div>
        </>
      ) : !errorMessage ? (
        <div className="gm-status__row">
          <span className="gm-status__dot" aria-hidden />
          <span>Checking your notifications...</span>
        </div>
      ) : null}
      {errorMessage ? <p className="gm-error">{errorMessage}</p> : null}
      <div className="gm-status__foot">
        {reach?.reachable ? (
          <span className="gm-status__ready">The game can reach you.</span>
        ) : (
          <span>{checking ? "Checking..." : "Not reachable yet."}</span>
        )}
        <button type="button" className="gm-link-button" onClick={onRecheck}>
          Check again
        </button>
      </div>
    </div>
  );
};

export default ReachStatus;
